import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
} from "@mui/material";

const PCP_TITLES = ["MD", "DO", "APRN", "PA", "NP"];

const PcpConfigFormDialog = ({
  open,
  onClose,
  onSave,
  pcpConfig = null,
  employees = [],
  locations = [],
  loading = false,
}) => {
  const isEdit = Boolean(pcpConfig);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          employeePcpId: pcpConfig.employeePcpId,
          locationId: pcpConfig.locationId,
          title: pcpConfig.title || "",
          highRisk: pcpConfig.highRisk ?? true,
          highCost: pcpConfig.highCost ?? true,
          medicare: pcpConfig.medicare ?? true,
          critical: pcpConfig.critical ?? false,
          ageMin: pcpConfig.ageMin ?? "",
          ageMax: pcpConfig.ageMax ?? "",
          maxPatient: pcpConfig.maxPatient ?? "",
          daysWork: pcpConfig.daysWork ?? "",
          employeeSupportId: pcpConfig.employeeSupportId || "",
        });
      } else {
        setForm({
          employeePcpId: "",
          locationId: "",
          title: "",
          highRisk: true,
          highCost: true,
          medicare: true,
          critical: false,
          ageMin: "",
          ageMax: "",
          maxPatient: "",
          daysWork: "",
          employeeSupportId: "",
        });
      }
      setError("");
    }
  }, [open, pcpConfig, isEdit]);

  const handleSubmit = () => {
    if (!isEdit && !form.employeePcpId) return setError("Employee is required");
    if (!isEdit && !form.locationId) return setError("Location is required");

    const data = {
      employeePcpId: Number(form.employeePcpId),
      locationId: Number(form.locationId),
      title: form.title || null,
      highRisk: form.highRisk,
      highCost: form.highCost,
      medicare: form.medicare,
      critical: form.critical,
      ageMin: form.ageMin !== "" ? Number(form.ageMin) : null,
      ageMax: form.ageMax !== "" ? Number(form.ageMax) : null,
      maxPatient: form.maxPatient !== "" ? Number(form.maxPatient) : null,
      daysWork: form.daysWork !== "" ? Number(form.daysWork) : null,
      employeeSupportId: form.employeeSupportId
        ? Number(form.employeeSupportId)
        : null,
    };

    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit PCP Configuration" : "New PCP Configuration"}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Employee selector (solo create) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small" disabled={isEdit}>
              <InputLabel>Employee *</InputLabel>
              <Select
                value={form.employeePcpId || ""}
                label="Employee *"
                onChange={(e) =>
                  setForm((p) => ({ ...p, employeePcpId: e.target.value }))
                }
              >
                {employees
                  .filter((e) => e.status === 1)
                  .map((e) => (
                    <MenuItem key={e.employeeId} value={e.employeeId}>
                      {e.firstName} {e.lastName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Location selector (solo create) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small" disabled={isEdit}>
              <InputLabel>Location *</InputLabel>
              <Select
                value={form.locationId || ""}
                label="Location *"
                onChange={(e) =>
                  setForm((p) => ({ ...p, locationId: e.target.value }))
                }
              >
                {locations
                  .filter((l) => l.status === 1 && l.clinical)
                  .map((l) => (
                    <MenuItem key={l.locationId} value={l.locationId}>
                      {l.medicalCenter}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Title</InputLabel>
              <Select
                value={form.title || ""}
                label="Title"
                onChange={(e) =>
                  setForm((p) => ({ ...p, title: e.target.value }))
                }
              >
                <MenuItem value="">None</MenuItem>
                {PCP_TITLES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Support PCP</InputLabel>
              <Select
                value={form.employeeSupportId || ""}
                label="Support PCP"
                onChange={(e) =>
                  setForm((p) => ({ ...p, employeeSupportId: e.target.value }))
                }
              >
                <MenuItem value="">None</MenuItem>
                {employees
                  .filter(
                    (e) =>
                      e.status === 1 &&
                      e.isPcp &&
                      e.employeeId !== Number(form.employeePcpId)
                  )
                  .map((e) => (
                    <MenuItem key={e.employeeId} value={e.employeeId}>
                      {e.firstName} {e.lastName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Age Min"
              value={form.ageMin ?? ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, ageMin: e.target.value }))
              }
              fullWidth
              size="small"
              type="number"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Age Max"
              value={form.ageMax ?? ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, ageMax: e.target.value }))
              }
              fullWidth
              size="small"
              type="number"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Max Patients"
              value={form.maxPatient ?? ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, maxPatient: e.target.value }))
              }
              fullWidth
              size="small"
              type="number"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Days/Week"
              value={form.daysWork ?? ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, daysWork: e.target.value }))
              }
              fullWidth
              size="small"
              type="number"
              slotProps={{ htmlInput: { min: 0, max: 7 } }}
            />
          </Grid>

          <Grid size={12}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.highRisk ?? true}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, highRisk: e.target.checked }))
                    }
                  />
                }
                label="High Risk"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.highCost ?? true}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, highCost: e.target.checked }))
                    }
                  />
                }
                label="High Cost"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.medicare ?? true}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, medicare: e.target.checked }))
                    }
                  />
                }
                label="Medicare"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.critical ?? false}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, critical: e.target.checked }))
                    }
                  />
                }
                label="Critical"
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PcpConfigFormDialog;
